// v-wp-tooltip: the one tooltip for element-level hover targets (a badge, an icon, a name in a
// grid cell, a toolbar button). It looks like the AG Grid column-header tooltip (HtmlTooltip):
// both include the same mixins from assets/css/mixins.scss.
//
//   v-wp-tooltip="'Plain text'"            text, always escaped
//   v-wp-tooltip.html="authoredHtml"       HTML, only for config-authored markup (never row data)
//   v-wp-tooltip.bottom="text"             preferred placement (flips when it does not fit)
//   v-wp-tooltip.overflow="text"           only while the element's text is cut off (ellipsis);
//                                          `{ content, overflow: '.selector' }` measures a child
//   v-wp-tooltip="{ content, html, placement, disabled, overflow }"
//   <WpTooltip> with a #content slot       rich Vue content (WpTooltip.vue)
//
// An empty / null content shows nothing, so `v-wp-tooltip="blurred ? null : text"` is the
// equivalent of the old conditional `:title`.
//
// ONE popper node on <body> serves every target, so hundreds of grid cells do not each mount
// a tooltip instance. Cells are recycled and virtually scrolled by AG Grid: the tooltip hides
// on scroll / wheel / resize / any pointer-down / Escape, when its target is unmounted, and
// when its target leaves the document, so no popper is ever left behind.
import { h, render as renderVNode } from 'vue';

import { TOOLTIP_SHOW_DELAY_MS } from '@/config/tooltip.js';

import './wpTooltip.scss';

const HOST_ID = 'wp-tooltip';
const TARGET_ATTR = 'data-wp-tooltip';
const GAP = 6;
const VIEWPORT_MARGIN = 8;
const CONNECTED_CHECK_MS = 250;

const states = new WeakMap();

let host = null;
let current = null;
let visible = false;
let showTimer = null;
let connectedTimer = null;
let hasRichContent = false;
let restoreDescribedBy = null;

// Rich content: the slot function renders inside the popper, with the owner's app context so
// its components, directives and i18n resolve.
const RenderSlot = (props) => props.render();

const ensureHost = () => {
  if (host?.isConnected) return host;
  host = document.createElement('div');
  host.id = HOST_ID;
  host.className = 'wp-tooltip';
  host.setAttribute('role', 'tooltip');
  document.body.appendChild(host);
  return host;
};

const clearContent = () => {
  if (!host) return;
  if (hasRichContent) renderVNode(null, host);
  hasRichContent = false;
  host.textContent = '';
};

const paint = (config) => {
  clearContent();
  host.classList.toggle('wp-tooltip--text', !config.render && !config.html);
  if (config.render) {
    const vnode = h(RenderSlot, { render: config.render });
    if (config.appContext) vnode.appContext = config.appContext;
    renderVNode(vnode, host);
    hasRichContent = true;
  } else if (config.html) {
    host.innerHTML = config.content;
  } else {
    host.textContent = config.content;
  }
};

const place = (el, placement) => {
  const rect = el.getBoundingClientRect();
  const width = host.offsetWidth;
  const height = host.offsetHeight;
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const above = rect.top - height - GAP;
  const below = rect.bottom + GAP;
  const fitsAbove = above >= VIEWPORT_MARGIN;
  const fitsBelow = below + height <= viewportHeight - VIEWPORT_MARGIN;
  let top;
  if (placement === 'bottom') top = fitsBelow || !fitsAbove ? below : above;
  else top = fitsAbove || !fitsBelow ? above : below;
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - height - VIEWPORT_MARGIN));
  const centered = rect.left + rect.width / 2 - width / 2;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(centered, viewportWidth - width - VIEWPORT_MARGIN));
  host.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
};

const onDismiss = () => hide();
const onKeydown = (event) => {
  if (event.key === 'Escape') hide();
};

const addDismissListeners = () => {
  window.addEventListener('scroll', onDismiss, { capture: true, passive: true });
  window.addEventListener('wheel', onDismiss, { passive: true });
  window.addEventListener('resize', onDismiss);
  window.addEventListener('blur', onDismiss);
  window.addEventListener('pointerdown', onDismiss, true);
  window.addEventListener('keydown', onKeydown, true);
};

const removeDismissListeners = () => {
  window.removeEventListener('scroll', onDismiss, { capture: true });
  window.removeEventListener('wheel', onDismiss);
  window.removeEventListener('resize', onDismiss);
  window.removeEventListener('blur', onDismiss);
  window.removeEventListener('pointerdown', onDismiss, true);
  window.removeEventListener('keydown', onKeydown, true);
};

// Whether the text the tooltip repeats is cut off: `overflow` is true (the target itself) or a
// selector for the child that holds the text.
const isCutOff = (el, overflow) => {
  const box = typeof overflow === 'string' ? el.querySelector(overflow) : el;
  if (!box) return false;
  // A line of text is often a pixel taller than its box (line-height rounding): only more than
  // that is a clamped line.
  return box.scrollWidth > box.clientWidth || box.scrollHeight > box.clientHeight + 1;
};

const reveal = () => {
  showTimer = null;
  const el = current;
  const config = el && states.get(el)?.config;
  if (!el || !config || !el.isConnected) {
    hide();
    return;
  }
  if (config.overflow && !isCutOff(el, config.overflow)) {
    hide();
    return;
  }
  ensureHost();
  paint(config);
  host.classList.add('wp-tooltip--visible');
  visible = true;
  place(el, config.placement);
  restoreDescribedBy = el.getAttribute('aria-describedby');
  el.setAttribute(
    'aria-describedby',
    restoreDescribedBy ? `${restoreDescribedBy} ${HOST_ID}` : HOST_ID,
  );
  // A row recycled without a Vue unmount (or a detached subtree) leaves no hook to hide on.
  connectedTimer = setInterval(() => {
    if (!current?.isConnected) hide();
  }, CONNECTED_CHECK_MS);
};

function hide(only) {
  if (only && current !== only) return;
  clearTimeout(showTimer);
  showTimer = null;
  clearInterval(connectedTimer);
  connectedTimer = null;
  if (current && visible) {
    if (restoreDescribedBy) current.setAttribute('aria-describedby', restoreDescribedBy);
    else current.removeAttribute('aria-describedby');
  }
  restoreDescribedBy = null;
  if (visible && host) {
    host.classList.remove('wp-tooltip--visible');
    clearContent();
  }
  if (current) removeDismissListeners();
  visible = false;
  current = null;
}

const start = (el, delay) => {
  if (!states.get(el)?.config) return;
  if (current && current !== el) hide();
  if (current === el && visible) return;
  if (!current) addDismissListeners();
  current = el;
  clearTimeout(showTimer);
  if (delay <= 0) reveal();
  else showTimer = setTimeout(reveal, delay);
};

const isFocusVisible = (el) => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

const normalize = (binding) => {
  const raw = binding.value;
  const options = raw !== null && typeof raw === 'object' ? raw : { content: raw };
  const renderFn = typeof options.render === 'function' ? options.render : null;
  const content = options.content == null || options.content === false ? '' : String(options.content);
  if (options.disabled || (!renderFn && content.trim() === '')) return null;
  return {
    content,
    // HTML is an explicit opt-in: the `.html` modifier or `html: true`.
    html: !renderFn && (options.html === true || binding.modifiers?.html === true),
    placement: options.placement ?? (binding.modifiers?.bottom ? 'bottom' : 'top'),
    render: renderFn,
    appContext: options.appContext ?? null,
    overflow: options.overflow || binding.modifiers?.overflow === true,
  };
};

const isHovered = (el) => {
  try {
    return el.matches(':hover');
  } catch {
    return false;
  }
};

const configure = (el, binding) => {
  const state = states.get(el);
  const hadConfig = !!state.config;
  state.config = normalize(binding);
  if (current !== el) {
    // Content that turns on while the pointer already rests on the target (a cell that drops
    // its tooltip while a thumbnail is hovered, then gets it back) has no new mouseenter.
    if (!hadConfig && state.config && !current && isHovered(el)) start(el, TOOLTIP_SHOW_DELAY_MS);
    return;
  }
  if (!state.config) {
    hide(el);
  } else if (visible) {
    paint(state.config);
    place(el, state.config.placement);
  }
};

export const vWpTooltip = {
  mounted(el, binding) {
    const state = {
      config: null,
      onEnter: () => start(el, TOOLTIP_SHOW_DELAY_MS),
      onLeave: (event) => {
        hide(el);
        // Leaving a nested target (a badge) back into the target around it (the cell): the
        // outer one gets no new mouseenter, so hand the hover back to it.
        const outer = event.relatedTarget?.closest?.(`[${TARGET_ATTR}]`);
        if (outer && outer !== el && outer.contains(el)) start(outer, TOOLTIP_SHOW_DELAY_MS);
      },
      onFocus: () => {
        if (isFocusVisible(el)) start(el, 0);
      },
      onBlur: () => hide(el),
    };
    states.set(el, state);
    el.setAttribute(TARGET_ATTR, '');
    configure(el, binding);
    el.addEventListener('mouseenter', state.onEnter);
    el.addEventListener('mouseleave', state.onLeave);
    el.addEventListener('focusin', state.onFocus);
    el.addEventListener('focusout', state.onBlur);
  },
  updated(el, binding) {
    if (states.has(el)) configure(el, binding);
  },
  unmounted(el) {
    const state = states.get(el);
    if (!state) return;
    hide(el);
    el.removeEventListener('mouseenter', state.onEnter);
    el.removeEventListener('mouseleave', state.onLeave);
    el.removeEventListener('focusin', state.onFocus);
    el.removeEventListener('focusout', state.onBlur);
    el.removeAttribute(TARGET_ATTR);
    states.delete(el);
  },
};

export const hideWpTooltip = () => hide();

// Removes the popper node (test teardown).
export const destroyWpTooltip = () => {
  hide();
  host?.remove();
  host = null;
};

export default (app) => {
  app.directive('wp-tooltip', vWpTooltip);
};
